import Modal from "Common/Components/Modal";
import React, { useState, useEffect } from "react";
import { CheckCircle, Loader2, Phone, User } from "lucide-react";
import Select from "react-select";
import { useAppServices } from "hook/services";
import { useParams } from "react-router-dom";

interface propTypes {
  handleRefresh: any;
  user: any;
}

const AssignSimModal = ({ handleRefresh, user }: propTypes) => {
  const AppService = useAppServices();
  const { locationId } = useParams();
  const [show, setShow] = useState<boolean>(false);
  const [availableSims, setAvailableSims] = useState<any[]>([]);
  const [selectedSim, setSelectedSim] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [assigning, setAssigning] = useState(false);

  const toggle = () => setShow((prev) => !prev);

  const fetchAvailableSims = async () => {
    setLoading(true);
    try {
      const { response } = await AppService.phone_bridge.sim.assignable_sim({
        query: `locationId=${locationId}`,
      });
      setAvailableSims(response?.data || []);
    } catch (error) {
      console.error("Error fetching SIMs:", error);
    } finally {
      setLoading(false);
    }
  };
  let pageLoad = true;
  useEffect(() => {
    if (pageLoad) {
      fetchAvailableSims();
      pageLoad = false;
    }
  }, []);

  const handleAssignSim = async () => {
    if (!user) return;

    setAssigning(true);
    const simIdToAssign =
      selectedSim?.value === "null" ? null : selectedSim?.value;

    try {
      const payload = {
        userId: user.id,
        simId: simIdToAssign,
      };
      const { response } = await AppService.phone_bridge.sim.assignSim({
        payload,
      });

      if (response) {
        await handleRefresh();
        toggle();
        fetchAvailableSims();
      }
    } catch (error) {
      console.error("Error assigning SIM to user:", error);
    } finally {
      setAssigning(false);
    }
  };

  const simOptions = [
    ...(user?.connectedSim ? [{ value: "null", label: "Remove SIM" }] : []),
    ...availableSims.map((sim) => ({
      value: sim._id,
      label: sim.sim_number,
    })),
  ];

  return (
    <>
      <button
        type="button"
        onClick={toggle}
        className="flex items-center gap-2 text-white text-sm px-3 py-1.5 rounded-md bg-custom-500 border border-custom-500 hover:bg-custom-600 focus:ring-2 focus:ring-custom-100 transition-all duration-200"
      >
        <Phone className="w-4 h-4" />
        {user?.connectedSim ? "Manage SIM" : "Assign SIM"}
      </button>

      <Modal
        show={show}
        onHide={toggle}
        id="deviceModal"
        modal-center="true"
        className="fixed flex flex-col transition-all duration-300 ease-in-out left-2/4 z-drawer -translate-x-2/4 -translate-y-2/4"
        dialogClassName="w-screen md:w-[40rem] bg-white shadow rounded-md dark:bg-zink-600"
      >
        <Modal.Header className="flex items-center justify-between p-4 border-b dark:border-zink-300/20">
          <Modal.Title className="text-16 font-semibold">
            Assign SIM
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="max-h-[calc(theme('height.screen')_-_180px)] p-4 overflow-y-auto">
          {user && (
            <div className="mb-4">
              <p className="text-gray-500 mb-2">Assigning SIM to:</p>
              <div className="flex items-center p-3 bg-gray-100 dark:bg-slate-800 rounded-xl">
                <div className="bg-primary/10 rounded-full p-2 me-3">
                  <User className="w-5 h-5 text-primary" />
                </div>
                <div className="flex-grow">
                  <h6 className="font-semibold text-gray-900 dark:text-white">
                    {user.username}
                  </h6>
                  <small className="text-gray-500">{user.email}</small>
                </div>
              </div>
            </div>
          )}

          <div className="mb-4">
            <label
              htmlFor="simSelect"
              className="block font-semibold text-gray-900 dark:text-white mb-2"
            >
              Select Available SIM
            </label>

            {loading ? (
              <div className="flex justify-center py-3">
                <Loader2 className="w-5 h-5 text-primary animate-spin" />
              </div>
            ) : (
              <Select
                id="simSelect"
                value={selectedSim}
                onChange={setSelectedSim}
                options={simOptions}
                placeholder="Choose a SIM..."
                isDisabled={assigning}
                className="text-gray-900 dark:text-white"
                styles={{
                  control: (base) => ({
                    ...base,
                    borderRadius: "0.75rem",
                    borderColor: "#e5e7eb",
                    backgroundColor: "white",
                    padding: "2px",
                  }),
                  menu: (base) => ({
                    ...base,
                    zIndex: 50,
                  }),
                }}
              />
            )}
          </div>
          <div className="flex justify-end">
            <button
              type="button"
              className={`flex items-center justify-center px-5 py-2 rounded-lg shadow-sm text-white font-medium transition-colors duration-200 ${
                assigning || loading || !selectedSim
                  ? "bg-custom-500/60 cursor-not-allowed"
                  : "bg-custom-500 hover:bg-custom-600"
              }`}
              onClick={handleAssignSim}
              disabled={assigning || loading || !selectedSim}
            >
              {assigning ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  {selectedSim?.value === "null" ? "Remove SIM" : "Assign SIM"}
                </>
              )}
            </button>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default AssignSimModal;
